import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Plus, CheckCircle2, History, BookOpen, Power } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Card, Button, Field, Input, Select, StatusBadge, Spinner, PageGuide } from "../../components/ui-kit";

const EVIDENCE_OPTIONS = [
  { value: "challenge_test", label: "Challenge test / prova di durabilità" },
  { value: "predictive_model", label: "Modello predittivo microbiologico" },
  { value: "literature", label: "Letteratura scientifica/tecnica" },
  { value: "expert_estimate", label: "Stima tecnica esperta" },
];

function err(e) { return formatApiErrorDetail(e.response?.data?.detail); }

// Tab generica per i 4 cataloghi semplici (Food Category, Process Type,
// Packaging, Storage Condition): stessa lista+form+toggle, campi diversi.
function SimpleCatalogTab({ endpoint, fields, itemLabel }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(Object.fromEntries(fields.map((f) => [f.key, f.type === "checkbox" ? false : ""])));

  const load = () => api.get(`/admin/shelf-life/${endpoint}`).then(({ data }) => setItems(data.items)).catch((e) => toast.error(err(e)));
  useEffect(() => { load(); }, [endpoint]);

  const create = async () => {
    const missing = fields.filter((f) => f.required && !String(form[f.key] || "").trim());
    if (missing.length) { toast.error(`Compila: ${missing.map((f) => f.label).join(", ")}`); return; }
    try {
      const payload = { ...form };
      fields.forEach((f) => { if (f.type === "number") payload[f.key] = payload[f.key] === "" ? null : Number(payload[f.key]); });
      await api.post(`/admin/shelf-life/${endpoint}`, payload);
      toast.success(`${itemLabel} creata`);
      setForm(Object.fromEntries(fields.map((f) => [f.key, f.type === "checkbox" ? false : ""])));
      load();
    } catch (e) { toast.error(err(e)); }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/admin/shelf-life/${endpoint}/${item.id}`, { active: !item.active });
      load();
    } catch (e) { toast.error(err(e)); }
  };

  if (!items) return <Spinner />;
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          {fields.map((f) => (
            <Field key={f.key} label={f.label + (f.required ? " *" : "")} help={f.help}>
              {f.type === "checkbox" ? (
                <label className="flex h-11 items-center gap-2 text-sm text-navy"><input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} /> {f.checkboxLabel}</label>
              ) : (
                <Input type={f.type || "text"} step={f.type === "number" ? "0.1" : undefined} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
              )}
            </Field>
          ))}
        </div>
        <div className="mt-3"><Button variant="success" onClick={create}><Plus className="w-4 h-4" /> Aggiungi {itemLabel.toLowerCase()}</Button></div>
      </Card>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className={`rounded-lg border p-3 text-sm ${it.active ? "border-hairline" : "border-slate-200 bg-slate-50 opacity-60"}`}>
            <div className="min-w-0">
              <div className="font-medium text-navy break-words">{it.name}{it.high_risk ? " · rischio elevato" : ""}</div>
              <div className="text-xs text-slate-400 break-words">{it.description}{it.temp_min_c !== undefined ? ` · ${it.temp_min_c}–${it.temp_max_c} °C` : ""}</div>
            </div>
            <div className="mt-2 flex justify-end">
              <Button variant="ghost" className="shrink-0" onClick={() => toggleActive(it)}><Power className="w-4 h-4" /> {it.active ? "Disattiva" : "Riattiva"}</Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-400">Nessun elemento in catalogo.</p>}
      </div>
    </div>
  );
}

function RulesTab() {
  const [categories, setCategories] = useState([]);
  const [processTypes, setProcessTypes] = useState([]);
  const [packagingTypes, setPackagingTypes] = useState([]);
  const [storageConditions, setStorageConditions] = useState([]);
  const [euReferences, setEuReferences] = useState([]);
  const [rules, setRules] = useState(null);
  const [versionsOf, setVersionsOf] = useState(null);
  const emptyRule = {
    food_category_id: "", process_type_ids: [], packaging_ids: [], storage_condition_id: "",
    ph_min: "", ph_max: "", aw_min: "", aw_max: "", reference_temp_c: "", base_shelf_life_days: "",
    q10: "2.0", evidence_level: "expert_estimate", regulatory_references: [], sources: [], notes: "",
    preservatives: [],
  };
  const [form, setForm] = useState(emptyRule);

  const loadAll = () => {
    Promise.all([
      api.get("/admin/shelf-life/categories"), api.get("/admin/shelf-life/process-types"),
      api.get("/admin/shelf-life/packaging"), api.get("/admin/shelf-life/storage-conditions"),
      api.get("/admin/shelf-life/eu-references"), api.get("/admin/shelf-life/rules"),
    ]).then(([cat, proc, pack, stor, refs, rul]) => {
      setCategories(cat.data.items); setProcessTypes(proc.data.items);
      setPackagingTypes(pack.data.items); setStorageConditions(stor.data.items);
      setEuReferences(refs.data.references); setRules(rul.data.rules);
    }).catch((e) => toast.error(err(e)));
  };
  useEffect(() => { loadAll(); }, []);

  const toggleMulti = (key, id) => {
    setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));
  };

  const addEuReference = (ref) => setForm((f) => ({ ...f, regulatory_references: [...f.regulatory_references, ref] }));

  const submitRule = async () => {
    if (!form.food_category_id || !form.reference_temp_c || !form.base_shelf_life_days) {
      toast.error("Categoria, temperatura di riferimento e shelf life di base sono obbligatorie"); return;
    }
    const payload = {
      ...form,
      ph_min: form.ph_min === "" ? null : Number(form.ph_min), ph_max: form.ph_max === "" ? null : Number(form.ph_max),
      aw_min: form.aw_min === "" ? null : Number(form.aw_min), aw_max: form.aw_max === "" ? null : Number(form.aw_max),
      reference_temp_c: Number(form.reference_temp_c), base_shelf_life_days: Number(form.base_shelf_life_days),
      q10: Number(form.q10),
    };
    try {
      await api.post("/admin/shelf-life/rules", payload);
      toast.success("Regola creata in bozza");
      setForm(emptyRule);
      loadAll();
    } catch (e) { toast.error(err(e)); }
  };

  const publish = async (ruleId) => {
    try { await api.post(`/admin/shelf-life/rules/${ruleId}/publish`); toast.success("Regola pubblicata"); loadAll(); }
    catch (e) { toast.error(err(e)); }
  };

  const showVersions = async (rule) => {
    try { const { data } = await api.get(`/admin/shelf-life/rules/${rule.id}/versions`); setVersionsOf({ groupId: rule.group_id, versions: data.versions }); }
    catch (e) { toast.error(err(e)); }
  };

  const categoryName = (id) => categories.find((c) => c.id === id)?.name || id;

  if (rules === null) return <Spinner />;
  return (
    <div className="space-y-5">
      <Card className="p-5">
        <h2 className="mb-3 font-heading text-base font-semibold text-navy">Nuova regola (bozza)</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Categoria alimentare *" help="La categoria di alimento a cui si applica questa regola (definita nella scheda “Categorie alimentari”).">
            <Select value={form.food_category_id} onChange={(e) => setForm({ ...form, food_category_id: e.target.value })}>
              <option value="">Seleziona...</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Conservazione (vuoto = qualunque)" help="Limita la regola a una specifica condizione di conservazione (es. refrigerato, congelato). Lascia vuoto se la regola vale per qualunque conservazione.">
            <Select value={form.storage_condition_id} onChange={(e) => setForm({ ...form, storage_condition_id: e.target.value })}>
              <option value="">Qualunque</option>
              {storageConditions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
          <Field label="Livello di evidenza" help="Su cosa si basa questo dato: una prova di durabilità reale, un modello predittivo, la letteratura tecnica o una stima esperta. Determina quanto è affidabile la regola.">
            <Select value={form.evidence_level} onChange={(e) => setForm({ ...form, evidence_level: e.target.value })}>
              {EVIDENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </Field>
          <Field label="Temperatura di riferimento (°C) *" help="La temperatura di conservazione a cui è validata la shelf life di base indicata sotto."><Input type="number" step="0.1" value={form.reference_temp_c} onChange={(e) => setForm({ ...form, reference_temp_c: e.target.value })} /></Field>
          <Field label="Shelf life di base (giorni) *" help="La durata in giorni alla temperatura di riferimento indicata sopra. Il motore la ricalcola automaticamente per altre temperature usando il fattore Q10."><Input type="number" value={form.base_shelf_life_days} onChange={(e) => setForm({ ...form, base_shelf_life_days: e.target.value })} /></Field>
          <Field label="Fattore Q10" hint="Sensibilità della crescita microbica alla temperatura, default 2.0" help="Indica di quanto accelera il deterioramento ogni 10°C in più. Il valore standard usato in letteratura è 2.0; aumentalo solo se hai un dato scientifico specifico per questo alimento."><Input type="number" step="0.1" value={form.q10} onChange={(e) => setForm({ ...form, q10: e.target.value })} /></Field>
          <Field label="pH minimo validato" help="Il pH più basso a cui è stata verificata la validità di questa regola. Lascia vuoto se non hai un dato validato."><Input type="number" step="0.01" value={form.ph_min} onChange={(e) => setForm({ ...form, ph_min: e.target.value })} /></Field>
          <Field label="pH massimo validato" help="Il pH più alto a cui è stata verificata la validità di questa regola. Lascia vuoto se non hai un dato validato."><Input type="number" step="0.01" value={form.ph_max} onChange={(e) => setForm({ ...form, ph_max: e.target.value })} /></Field>
          <Field label="aw minima validata" help="L’attività dell’acqua (aw) minima a cui è stata verificata la regola, se disponibile."><Input type="number" step="0.01" value={form.aw_min} onChange={(e) => setForm({ ...form, aw_min: e.target.value })} /></Field>
          <Field label="aw massima validata" help="L’attività dell’acqua (aw) massima a cui è stata verificata la regola, se disponibile."><Input type="number" step="0.01" value={form.aw_max} onChange={(e) => setForm({ ...form, aw_max: e.target.value })} /></Field>
        </div>

        <div className="mt-4">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Tipi di processo applicabili (vuoto = qualunque)</div>
          <div className="flex flex-wrap gap-2">
            {processTypes.map((p) => (
              <button key={p.id} type="button" onClick={() => toggleMulti("process_type_ids", p.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium ${form.process_type_ids.includes(p.id) ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{p.name}</button>
            ))}
            {processTypes.length === 0 && <span className="text-xs text-slate-400">Nessun tipo di processo in catalogo.</span>}
          </div>
        </div>
        <div className="mt-3">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Imballaggi applicabili (vuoto = qualunque)</div>
          <div className="flex flex-wrap gap-2">
            {packagingTypes.map((p) => (
              <button key={p.id} type="button" onClick={() => toggleMulti("packaging_ids", p.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium ${form.packaging_ids.includes(p.id) ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{p.name}</button>
            ))}
            {packagingTypes.length === 0 && <span className="text-xs text-slate-400">Nessun imballaggio in catalogo.</span>}
          </div>
        </div>

        <div className="mt-4">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><BookOpen className="h-3.5 w-3.5" /> Riferimenti normativi</div>
          <div className="flex flex-wrap gap-2 mb-2">
            {euReferences.map((r, i) => (
              <button key={i} type="button" onClick={() => addEuReference(r)} className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100">+ {r.law}</button>
            ))}
          </div>
          {form.regulatory_references.map((r, i) => (
            <div key={i} className="mb-1 rounded-lg bg-slate-50 px-3 py-1.5 text-xs text-navy">{r.law} — {r.article} {r.note ? `: ${r.note}` : ""}</div>
          ))}
        </div>

        <div className="mt-4">
          <Field label="Note tecniche" help="Annotazioni interne per chi gestisce il catalogo: fonti, dubbi, condizioni particolari. Non è visibile ai clienti."><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
        <div className="mt-4"><Button variant="success" onClick={submitRule}><Plus className="w-4 h-4" /> Crea regola in bozza</Button></div>
      </Card>

      <Card className="p-5">
        <h2 className="mb-3 font-heading text-base font-semibold text-navy">Regole esistenti</h2>
        <div className="space-y-2">
          {rules.map((r) => (
            <div key={r.id} className="rounded-lg border border-hairline p-3 text-sm">
              <div className="min-w-0">
                <div className="font-medium text-navy break-words">{categoryName(r.food_category_id)} — v{r.version} — {r.base_shelf_life_days}g @ {r.reference_temp_c}°C</div>
                <div className="text-xs text-slate-400 break-words">{r.evidence_level} · creata {new Date(r.created_at).toLocaleDateString("it-IT")}</div>
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <StatusBadge status={r.status === "pubblicata" ? "verde" : r.status === "bozza" ? "blu" : "grigio"}>{r.status}</StatusBadge>
                <div className="flex flex-wrap items-center gap-2">
                  {r.status !== "pubblicata" && <Button variant="outline" className="shrink-0" onClick={() => publish(r.id)}><CheckCircle2 className="w-4 h-4" /> Pubblica</Button>}
                  <Button variant="ghost" className="shrink-0" onClick={() => showVersions(r)}><History className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
          {rules.length === 0 && <p className="text-sm text-slate-400">Nessuna regola creata.</p>}
        </div>
      </Card>

      {versionsOf && (
        <Card className="p-5">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold text-navy">Versioni della regola</h3>
            <Button variant="ghost" onClick={() => setVersionsOf(null)}>Chiudi</Button>
          </div>
          {versionsOf.versions.map((v) => (
            <div key={v.id} className="flex items-center justify-between border-b border-hairline py-2 text-sm last:border-0">
              <span>v{v.version} — {v.base_shelf_life_days}g @ {v.reference_temp_c}°C</span>
              <StatusBadge status={v.status === "pubblicata" ? "verde" : v.status === "bozza" ? "blu" : "grigio"}>{v.status}</StatusBadge>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

const GUIDES = {
  categories: {
    summary: "Le categorie alimentari sono la base su cui poggiano le regole di shelf life: ogni regola si applica a una categoria.",
    goals: [
      "Definire le famiglie di alimenti (es. “Prodotti da forno freschi”) su cui si baseranno le regole di shelf life.",
      "Segnalare quali categorie sono a rischio elevato (RTE / Listeria) perché il motore le tratti con maggiore cautela.",
    ],
    steps: [
      "Inserisci un nome chiaro e, se utile, una breve descrizione.",
      "Se conosci una durata indicativa, inseriscila in “Shelf life di default”: verrà proposta finché non esiste una regola pubblicata specifica per questa categoria.",
      "Segna “Rischio elevato” solo per alimenti pronti al consumo che possono favorire la Listeria.",
    ],
  },
  process: {
    summary: "I tipi di processo descrivono la lavorazione subita dal prodotto e possono essere richiesti come condizione da una regola.",
    goals: ["Catalogare le lavorazioni (cottura, pastorizzazione, confezionamento sottovuoto, ecc.) usate dalle regole di shelf life."],
    steps: ["Inserisci il nome del processo e, se utile, una descrizione con i parametri tipici (es. tempo/temperatura)."],
  },
  packaging: {
    summary: "Gli imballaggi influenzano la velocità di deterioramento del prodotto e possono essere richiesti come condizione da una regola.",
    goals: ["Catalogare i tipi di imballaggio (sottovuoto, ATM, aria) usati dalle regole di shelf life."],
    steps: ["Inserisci il nome dell’imballaggio e, se applicabile, il tipo di atmosfera nella confezione."],
  },
  storage: {
    summary: "Le condizioni di conservazione fissano il range di temperatura entro cui una regola è valida.",
    goals: ["Catalogare le condizioni di conservazione (refrigerato, congelato, ambiente) con i relativi limiti di temperatura."],
    steps: ["Inserisci nome e temperatura minima/massima: sono obbligatori e vengono usati per validare le regole collegate."],
  },
  rules: {
    summary: "Qui crei le regole vere e proprie: sono ciò che il motore usa davvero per calcolare la shelf life dei prodotti dei clienti.",
    goals: [
      "Creare e pubblicare le regole che il motore userà per calcolare la shelf life dei prodotti dei clienti.",
      "Tracciare le versioni di ogni regola nel tempo, mantenendo uno storico verificabile e consultabile.",
    ],
    steps: [
      "Seleziona la categoria alimentare (obbligatoria) e, se pertinente, la condizione di conservazione.",
      "Indica il livello di evidenza scientifica su cui si basa il dato: più è alto, più la regola è affidabile.",
      "Inserisci temperatura di riferimento e shelf life di base: sono i due valori da cui il motore calcola tutto il resto.",
      "Regola il fattore Q10 solo se hai un dato specifico diverso dallo standard 2.0.",
      "Aggiungi, se disponibili, i limiti di pH/aw, i tipi di processo, gli imballaggi applicabili e i riferimenti normativi.",
      "Crea la regola in bozza, verificala con calma, poi premi “Pubblica” per renderla attiva nel motore.",
    ],
  },
};

export default function ShelfLifeRulesAdmin() {
  const [tab, setTab] = useState("categories");
  const tabs = [
    { key: "categories", label: "Categorie alimentari" },
    { key: "process", label: "Tipi di processo" },
    { key: "packaging", label: "Imballaggi" },
    { key: "storage", label: "Conservazione" },
    { key: "rules", label: "Regole" },
  ];
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-navy text-gold-bright"><FlaskConical className="h-5 w-5" /></div>
        <div>
          <h1 className="font-heading text-xl font-bold text-navy">DF Food Safety Expert Engine — Shelf Life</h1>
          <p className="text-sm text-slate-500">Cataloghi tecnico-normativi condivisi da tutti i clienti e regole versionate del motore.</p>
        </div>
      </div>
      <PageGuide title={`Guida rapida: ${tabs.find((t) => t.key === tab)?.label}`} {...GUIDES[tab]} testid="shelf-life-guide" />
      <div className="flex flex-wrap gap-2 border-b border-hairline pb-2">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-lg px-3 py-2 text-sm font-medium ${tab === t.key ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-100"}`}>{t.label}</button>
        ))}
      </div>
      {tab === "categories" && <SimpleCatalogTab endpoint="categories" itemLabel="Categoria" fields={[
        { key: "name", label: "Nome", required: true, help: "Il nome della categoria di alimento, es. “Prodotti da forno freschi” o “Piatti pronti refrigerati”. Comparirà nel selettore delle regole." },
        { key: "description", label: "Descrizione", help: "Una breve descrizione per chiarire cosa rientra in questa categoria, utile a chi la userà in futuro." },
        { key: "default_shelf_life_days", label: "Shelf life di default (giorni)", type: "number", help: "Una durata indicativa da proporre quando non esiste ancora una regola specifica pubblicata per questa categoria." },
        { key: "high_risk", label: "Rischio elevato", type: "checkbox", checkboxLabel: "RTE / supporta Listeria", help: "Segna questa casella per gli alimenti pronti al consumo (RTE) che possono favorire la crescita di Listeria monocytogenes: vengono trattati con maggiore cautela dal motore." },
      ]} />}
      {tab === "process" && <SimpleCatalogTab endpoint="process-types" itemLabel="Tipo di processo" fields={[
        { key: "name", label: "Nome", required: true, help: "Il tipo di lavorazione o trattamento subito dal prodotto, es. “Cottura”, “Pastorizzazione”, “Confezionamento sottovuoto”." }, { key: "description", label: "Descrizione", help: "Dettagli utili sul processo, es. parametri tipici di tempo/temperatura." },
      ]} />}
      {tab === "packaging" && <SimpleCatalogTab endpoint="packaging" itemLabel="Imballaggio" fields={[
        { key: "name", label: "Nome", required: true, help: "Il tipo di confezionamento del prodotto, es. “Sottovuoto”, “Vaschetta in aria”, “ATM”." }, { key: "description", label: "Descrizione", help: "Dettagli utili sull’imballaggio, se servono a chiarire il tipo." },
        { key: "atmosphere", label: "Atmosfera (aria/sottovuoto/ATM)", help: "Il tipo di atmosfera all’interno della confezione: incide sulla velocità di deterioramento del prodotto." },
      ]} />}
      {tab === "storage" && <SimpleCatalogTab endpoint="storage-conditions" itemLabel="Condizione di conservazione" fields={[
        { key: "name", label: "Nome", required: true, help: "Il nome della condizione di conservazione, es. “Refrigerato”, “Congelato”, “Temperatura ambiente”." },
        { key: "temp_min_c", label: "Temp. minima (°C)", type: "number", required: true, help: "La temperatura minima prevista per questa condizione di conservazione." },
        { key: "temp_max_c", label: "Temp. massima (°C)", type: "number", required: true, help: "La temperatura massima prevista per questa condizione di conservazione." },
        { key: "description", label: "Descrizione", help: "Dettagli utili sulla condizione di conservazione, se necessari." },
      ]} />}
      {tab === "rules" && <RulesTab />}
    </div>
  );
}
